export const PRODUCT_HIGHLIGHT_LABELS = {
  color: 'Cor',
  power: 'Potência',
  warranty: 'Garantia',
  connector: 'Conector',
};

const LEGACY_TOP_LEVEL_ALIASES = {
  color: ['colour', 'cor'],
  power: ['charger_watts', 'wattage', 'potencia', 'potência'],
  warranty: ['garantia', 'warranty_days', 'warranty_months'],
  connector: ['connector_type', 'connection', 'conector'],
};

const KNOWN_DYNAMIC_PATHS = {
  color: [['color'], ['colour'], ['cor']],
  power: [['potencia', 'watts'], ['potência', 'watts'], ['charger_watts'], ['wattage'], ['power'], ['potencia'], ['potência']],
  warranty: [['info', 'garantia_meses'], ['warranty'], ['garantia'], ['warranty_months']],
  connector: [['saida', 'tipo_conector'], ['conectividade', 'tipo_conector'], ['connector_type'], ['connector'], ['connection'], ['conector']],
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const cleaned = value.map(normalizeText).filter(Boolean);
    return cleaned.length ? Array.from(new Set(cleaned)).join(', ') : null;
  }
  if (typeof value === 'object') return null;
  const text = String(value).trim().replace(/\s+/g, ' ');
  if (!text) return null;
  if (['null', 'undefined', 'none', 'nan'].includes(text.toLowerCase())) return null;
  return text;
};

const readPath = (data, path) => path.reduce((current, segment) => (
  current && typeof current === 'object' ? current[segment] : undefined
), data);

const recursiveFindByKeys = (data, keys) => {
  if (Array.isArray(data)) {
    for (const item of data) {
      const found = recursiveFindByKeys(item, keys);
      if (found !== undefined && found !== null) return found;
    }
    return null;
  }

  if (!data || typeof data !== 'object') return null;
  for (const key of keys) {
    if (normalizeText(data[key])) return data[key];
  }
  for (const value of Object.values(data)) {
    const found = recursiveFindByKeys(value, keys);
    if (found !== undefined && found !== null) return found;
  }
  return null;
};

const formatPower = (value) => {
  const text = normalizeText(value);
  if (!text) return null;
  return text.toUpperCase().endsWith('W') ? text : `${text}W`;
};

const formatWarrantyMonths = (value) => {
  const text = normalizeText(value);
  if (!text) return null;
  return /(mês|mes|ano|dia)/i.test(text) ? text : `${text} meses`;
};

const formatWarrantyDays = (value) => {
  const text = normalizeText(value);
  if (!text) return null;
  return /dia/i.test(text) ? text : `${text} dias`;
};

export const buildProductHighlightPayload = (values = {}) => ({
  color: normalizeText(values.color),
  power: normalizeText(values.power),
  warranty: normalizeText(values.warranty),
  connector: normalizeText(values.connector),
});

export const resolveProductHighlightValue = (product = {}, canonicalKey) => {
  if (Object.prototype.hasOwnProperty.call(product || {}, canonicalKey)) {
    return normalizeText(product?.[canonicalKey]);
  }

  const dynamicSources = [product?.specs, product?.technical_specs, product?.attributes, product?.dynamic_attributes]
    .filter((item) => item && typeof item === 'object');

  for (const dynamicSource of dynamicSources) {
    for (const path of KNOWN_DYNAMIC_PATHS[canonicalKey] || []) {
      const rawValue = readPath(dynamicSource, path);
      if (rawValue === undefined || rawValue === null) continue;
      const formatted = canonicalKey === 'power' && ['watts', 'charger_watts', 'wattage'].includes(path[path.length - 1])
        ? formatPower(rawValue)
        : canonicalKey === 'warranty' && path[path.length - 1] === 'garantia_meses'
          ? formatWarrantyMonths(rawValue)
          : normalizeText(rawValue);
      if (formatted) return formatted;
    }

    const aliases = [canonicalKey, ...(LEGACY_TOP_LEVEL_ALIASES[canonicalKey] || [])];
    const recursiveValue = recursiveFindByKeys(dynamicSource, aliases);
    if (canonicalKey === 'power') {
      const formatted = formatPower(recursiveValue);
      if (formatted) return formatted;
    }
    const normalizedRecursive = normalizeText(recursiveValue);
    if (normalizedRecursive) return normalizedRecursive;
  }

  for (const alias of LEGACY_TOP_LEVEL_ALIASES[canonicalKey] || []) {
    const rawValue = product?.[alias];
    if (rawValue === undefined || rawValue === null) continue;
    const formatted = canonicalKey === 'power' && ['charger_watts', 'wattage'].includes(alias)
      ? formatPower(rawValue)
      : canonicalKey === 'warranty' && alias === 'warranty_days'
        ? formatWarrantyDays(rawValue)
        : canonicalKey === 'warranty' && alias === 'warranty_months'
          ? formatWarrantyMonths(rawValue)
          : normalizeText(rawValue);
    if (formatted) return formatted;
  }

  return null;
};

export const buildProductHighlightItems = (product = {}) => {
  const resolved = {
    color: resolveProductHighlightValue(product, 'color'),
    power: resolveProductHighlightValue(product, 'power'),
    warranty: resolveProductHighlightValue(product, 'warranty'),
    connector: resolveProductHighlightValue(product, 'connector'),
  };
  return Object.entries(resolved)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => ({ key, label: PRODUCT_HIGHLIGHT_LABELS[key], value }));
};

export const buildProductHighlightSummary = (product = {}) => buildProductHighlightItems(product)
  .map((item) => item.value)
  .join(' • ');